import { useCallback, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';

import { doRequest } from '@r1-frontend/do-request';
import { IPayResponse, pay } from '@r1-frontend/api-domru/api-loyalty/v1/program/pay';

import { btnSizes, btnTypes } from '@r1-frontend/ui-react/components/buttons/baseButton';
import { wideBreakpoints } from '@r1-frontend/ui-react/components/layouts/wideContainer';
import { PolicyNote } from '@r1-frontend/ui-react/components/PolicyNote';
import { H3 } from '@r1-frontend/ui-react/components/typography/heading';
import { FullWidthListContainer } from '@r1-frontend/ui-react/experimental/containers';
import { RubleSymbol } from '@r1-frontend/ui-react/experimental/purchase/cost/Cost/RubleSymbol';
import { useMatchMedia } from '@r1-frontend/shared/hooks/useMatchMedia';

import { POLICY_PAGE_LINK } from '~/src/constants';
import { selectCardPaySum } from '~/src/features/payments-abn/components/SeasonTicketPayment/store/selectors/selectPaymentSum';
import { pushDataLayer } from '~/src/features/payments-abn/helpers/pushDataLayer';
import { IPaymentResult, setPaymentResult } from '~/src/features/payments-abn/store/actionCreators';
import {
    bindedCardAutoPayConnect,
    bindedCardPayment,
    newCardAutoPayConnect,
    newCardPayment,
} from '~/src/features/payments-abn/store/actions';
import { selectIsPaymentValid } from '~/src/features/payments-abn/store/selectors/selectIsPaymentValid';
import { IQueryPaymentParams } from '~/src/features/payments-abn/types';
import { getAnalyticsSender } from '~/src/helpers/hooks/useAnaliticsSender';
import { TState } from '~/src/store';
import { getClientAll } from '~/src/store/actions/auth/getClientAll';
import { getLoyaltyInfo } from '~/src/store/actions/loyalty';
import selectProviderId from '~/src/store/selectors/selectProviderId';

import { useCardPayment } from './useCardPayment';

import { PayButton } from './styled';

/**
 * Оплата с помощью карты
 * Реализует оплату новой картой и ранее сохраненной картой
 * @param children - для прокидывания формы оплаты
 * @todo разделить оплату новой и оплату сохраненной картой добавить фрейм <PaymentFrame/> в компонент для оплаты новой картой
 */
const CardPayment = ({ children }: { children?: JSX.Element }): JSX.Element | null => {
    /**
     * @param requestId используется при покупке оборудования (доставка), для прокидывания идентификатора заказа в "платеж"
     */

    const { query: { requestId = '', transaction, s: queryPaySum } } = useRouter<IQueryPaymentParams>();

    useCardPayment();

    const {
        auth: { accessToken, isAuth },
        payment: {
            paymentData: { paymentSum, isUseBonuses, bonusesValue, isConnectAutoPay },
            isSaveCard,
            method,
            result,
            analitycsData,
        },
        isValid,
        providerId,
        cardPaymentSum,
    } = useSelector((state: TState) => ({
        auth: state.auth,
        payment: state.payment,
        isValid: selectIsPaymentValid(state),
        providerId: selectProviderId(state),
        cardPaymentSum: selectCardPaySum(state),
    }), shallowEqual);

    const sendAnalytics = getAnalyticsSender(analitycsData?.category || '');

    const isOnlyBonuses = !cardPaymentSum && isUseBonuses && !!bonusesValue;

    const dispatch = useDispatch();

    const isMobile = useMatchMedia(`(max-width: ${wideBreakpoints.mobile})`);

    const [isLoading, setIsLoading] = useState(false);

    const methodType = method?.methodType;
    const isAutoPayConnected = method?.isAutoPayConnected;

    const onBonusesPayment = useCallback(async() => {
        const isOnlyBonusesPayment = isOnlyBonuses && ((methodType === 'new-card' && !isSaveCard) || (methodType === 'card' && !isConnectAutoPay));

        const resp = await doRequest.apiLoyalty<IPayResponse>(
            pay(providerId, { bonusValue: bonusesValue }),
        );

        const respData: IPaymentResult = {
            ...result,
            isBonusesSuccess: resp.isSuccess,
            isOpenPopup: isOnlyBonusesPayment,
        };
        dispatch(getLoyaltyInfo());
        dispatch(setPaymentResult(respData));
        // TODO прояснить для чего выполняется
        if (isOnlyBonusesPayment) {
            dispatch(getClientAll());
        }
    }, [accessToken, dispatch, isConnectAutoPay, isOnlyBonuses, isSaveCard, methodType, bonusesValue, result]);

    const onNewCardPayment = useCallback(() => {
        pushDataLayer('click payment by new card', isSaveCard ? `save card${isConnectAutoPay ? ' and autopayment on' : ''}` : '');

        if (cardPaymentSum) {
            dispatch(newCardPayment({ deliveryRequestId: requestId }));
        } else if (isConnectAutoPay || isSaveCard) {
            dispatch(newCardAutoPayConnect());
        }
    }, [isSaveCard, isConnectAutoPay, cardPaymentSum, requestId, dispatch]);

    const onBindedCardPayment = useCallback(() => {
        pushDataLayer('click payment by old card', isConnectAutoPay ? 'autopayment on' : '');

        if (cardPaymentSum) {
            dispatch(bindedCardPayment({ deliveryRequestId: requestId, transaction, transactionSum: queryPaySum }));
        } else if (isConnectAutoPay) {
            dispatch(bindedCardAutoPayConnect());
        }
    }, [isConnectAutoPay, cardPaymentSum, requestId, transaction, queryPaySum, dispatch]);

    const onClickHandler = useCallback(async() => {
        // TODO перенести логику оплаты в thunk-экшен
        setIsLoading(true);

        sendAnalytics('click_payment', analitycsData?.label);

        if (isUseBonuses && bonusesValue) {
            await onBonusesPayment();
        }

        if (cardPaymentSum || isConnectAutoPay || isSaveCard) {
            methodType === 'card' ? onBindedCardPayment() : onNewCardPayment();
        }

        setIsLoading(false);
    }, [
        cardPaymentSum,
        bonusesValue,
        methodType,
        isSaveCard,
        isUseBonuses,
        isConnectAutoPay,
        analitycsData?.label,
        onBonusesPayment,
        onNewCardPayment,
        onBindedCardPayment,
        sendAnalytics,
    ]);

    const currentBtnText = useMemo(() => {
        if (!paymentSum) {
            return isConnectAutoPay ? 'Подключить автоплатёж' : 'Оплатить';
        }
        if (isConnectAutoPay && !isMobile && !isAutoPayConnected && !requestId) {
            return isOnlyBonuses ? 'Оплатить бонусами и подключить автоплатёж' : `Оплатить ${paymentSum} ${RubleSymbol} и подключить автоплатёж`;
        } else {
            return isOnlyBonuses ? 'Оплатить бонусами' : `Оплатить ${paymentSum} ${RubleSymbol}`;
        }
    }, [isConnectAutoPay, isAutoPayConnected, isMobile, isOnlyBonuses, paymentSum, requestId]);

    if (!(method?.methodType === 'card' || method?.methodType === 'new-card')) {
        return null;
    }

    return (
        <FullWidthListContainer $direction='column' $size={24}>
            <H3 $withIndent={false}>Детали оплаты</H3>

            {children}

            <FullWidthListContainer $direction='column' $size={8}>
                {!isAuth &&
                    <PolicyNote
                        data-test='pay-agreement'
                        buttonText='Оплатить'
                        agreeUrl={POLICY_PAGE_LINK}
                    />
                }
                <PayButton
                    type={btnTypes.ACCENT}
                    size={btnSizes.LARGE}
                    text={currentBtnText}
                    onClick={onClickHandler}
                    isLoading={isLoading}
                    disabled={!isValid}
                    isOnlyBonuses={isOnlyBonuses && isValid}
                    data-test='pay-button'
                />
            </FullWidthListContainer>
        </FullWidthListContainer>
    );
};

export default CardPayment;
